import type { Dispatch, FC, ReactNode, SetStateAction } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash';

import { DashboardWidgetType } from 'src/constants/dashboard';
import type {
  CodingResult,
  DashboardWidgetConfig,
  GroupedCodingResult,
} from 'src/types/dashboard';
import useDebounce, { debounceTimes } from 'src/hooks/useDebounce';
import { groupCalculatedCodingResults } from 'src/components/dashboard/DashboardContext/CodingResultsContext/groupCalculatedCodingResult';
import { dashboardContainsWidgetTypes } from 'src/components/dashboard/DashboardContext/helper';
import { useDashboardContext } from 'src/components/dashboard/DashboardContext/index';
import type { OpenCommentFilter } from 'src/components/dashboard/DashboardPage/widgets/OpenCommentsWidget/types';
import type { NoData } from 'src/components/dashboard/hooks/helper';
import { getNoData } from 'src/components/dashboard/hooks/helper';
import useCodingResultsQuery from 'src/components/dashboard/hooks/useCodingResultsQuery';

export interface CodingResultsValue {
  codingResults: CodingResult[];
  groupedCodingResults: GroupedCodingResult[];
  setTextFilter: Dispatch<SetStateAction<string>>;
  textFilter: string;
  textFilterLoading: boolean;
  debouncedTextFilter: string;
  filter: OpenCommentFilter;
  setFilter: Dispatch<SetStateAction<OpenCommentFilter>>;
  noData: NoData;
  errorMsg: string;
  loading: boolean;
  pending: boolean;
}

export const CodingResultsContext = createContext<CodingResultsValue>({
  codingResults: [],
  groupedCodingResults: [],
  setTextFilter: () => {},
  textFilter: '',
  textFilterLoading: false,
  debouncedTextFilter: '',
  filter: {
    topicFilter: 'all',
    sentimentFilter: 'totalOfAllCounts',
    sorting: 'frequency',
  },
  setFilter: () => null,
  noData: { initial: false, filtered: false },
  errorMsg: '',
  loading: false,
  pending: false,
});

const initialFilter: OpenCommentFilter = {
  topicFilter: 'all',
  sentimentFilter: 'totalOfAllCounts',
  sorting: 'frequency',
};

const codingResultsWidgets = [
  DashboardWidgetType.INSIGHT_WHEEL,
  DashboardWidgetType.OPEN_COMMENTS,
];

export const CodingResultsContextProvider: FC<{
  config?: DashboardWidgetConfig;
  children?: ReactNode;
}> = ({ config, children }) => {
  const { t } = useTranslation('common');
  const { dashboardConfig } = useDashboardContext();
  const [textFilter, setTextFilter] = useState('');
  const debouncedTextFilter = useDebounce(
    textFilter,
    debounceTimes.TEXT_FILTER,
  );
  const [filter, setFilter] = useState<OpenCommentFilter>(initialFilter);

  const isEnabled = useMemo(
    () => dashboardContainsWidgetTypes(dashboardConfig, codingResultsWidgets),
    [dashboardConfig],
  );
  const { data, isFetching, isLoading, error } = useCodingResultsQuery(
    isEnabled,
    config ? config.questions?.[0] : null,
    dashboardConfig?.id,
    debouncedTextFilter,
  );
  const loading = isFetching || isLoading;
  const textFilterLoading = textFilter !== debouncedTextFilter;

  const codingResults = useMemo(() => data ?? [], [data]);
  const groupedCodingResults: GroupedCodingResult[] = useMemo(
    () => groupCalculatedCodingResults(codingResults),
    [codingResults, dashboardConfig],
  );

  const noData = useMemo(
    () =>
      getNoData(
        loading,
        !!codingResults.length,
        isEqual(filter, initialFilter) && debouncedTextFilter === '',
      ),
    [loading, codingResults, filter, debouncedTextFilter],
  );

  const errorMsg = useMemo(() => {
    if (!error) {
      // if noData is not set but there are still no coding results due to the filter settings an error is returned
      if (!noData && !codingResults.length) return t('noResults');
      return '';
    }

    switch (error) {
      case 404:
        return t('pageNotFound.headline');
      case 401:
        return t('pageNotFound.accessDenied');
      default:
        return t('unknownError');
    }
  }, [error, codingResults, noData]);

  const pending = useMemo(
    () => isEnabled && isLoading && !codingResults.length,
    [isEnabled, codingResults],
  );

  return (
    <CodingResultsContext.Provider
      value={{
        codingResults,
        groupedCodingResults,
        setTextFilter,
        textFilter,
        textFilterLoading,
        debouncedTextFilter,
        noData,
        errorMsg,
        loading,
        pending,
        filter,
        setFilter,
      }}
    >
      {children}
    </CodingResultsContext.Provider>
  );
};

export const useCodingResults = () => useContext(CodingResultsContext);
